import { useCallback, useEffect, useRef } from "react";
import { useConsultationAudioArchive } from "./audioArchive";

interface UseConsultationAudioArchiveRecorderParams {
  clinicId: string | null;
  petId: string;
}

interface StartAudioArchiveParams {
  consultationTranscriptionId: string | null;
  stream?: MediaStream;
}

export const useConsultationAudioArchiveRecorder = ({
  clinicId,
  petId,
}: UseConsultationAudioArchiveRecorderParams) => {
  const archive = useConsultationAudioArchive();
  const {
    archiveState,
    prepareNewSession,
    start: startArchive,
    pause: pauseArchive,
    resume: resumeArchive,
    finalize,
    enqueueBackgroundUpload,
    cancelAndPurge,
  } = archive;
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const startPromiseRef = useRef<Promise<boolean> | null>(null);
  const isArchiveActiveRef = useRef(false);

  const stopOwnedStream = useCallback(() => {
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
  }, []);

  const start = useCallback(async ({ consultationTranscriptionId, stream }: StartAudioArchiveParams) => {
    if (startPromiseRef.current) {
      return startPromiseRef.current;
    }

    const startPromise = (async () => {
      if (!clinicId || !petId) {
        return false;
      }

      if (archiveState === "paused") {
        resumeArchive();
        return true;
      }

      try {
        await prepareNewSession();

        const resolvedStream = stream ?? await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });

        mediaStreamRef.current = resolvedStream;

        const started = await startArchive(resolvedStream, {
          consultationTranscriptionId,
          clinicId,
          petId,
        });

        if (!started) {
          stopOwnedStream();
        }

        isArchiveActiveRef.current = started;
        return started;
      } catch (error) {
        console.warn("Audio archive external recorder start failed", error);
        isArchiveActiveRef.current = false;
        stopOwnedStream();
        return false;
      }
    })().finally(() => {
      startPromiseRef.current = null;
    });

    startPromiseRef.current = startPromise;
    return startPromise;
  }, [archiveState, clinicId, petId, prepareNewSession, resumeArchive, startArchive, stopOwnedStream]);

  const pause = useCallback(() => {
    pauseArchive();
  }, [pauseArchive]);

  const resume = useCallback(() => {
    resumeArchive();
  }, [resumeArchive]);

  const cloneStream = useCallback(() => {
    return mediaStreamRef.current?.clone() ?? null;
  }, []);

  const stopAndUpload = useCallback(() => {
    void (async () => {
      if (startPromiseRef.current) {
        await startPromiseRef.current;
      }

      if (!isArchiveActiveRef.current) {
        return;
      }

      const session = await finalize();
      isArchiveActiveRef.current = false;
      await enqueueBackgroundUpload(session);
    })()
      .finally(() => {
        stopOwnedStream();
      });
  }, [enqueueBackgroundUpload, finalize, stopOwnedStream]);

  const cancel = useCallback(() => {
    startPromiseRef.current = null;
    isArchiveActiveRef.current = false;
    void cancelAndPurge().finally(() => {
      stopOwnedStream();
    });
  }, [cancelAndPurge, stopOwnedStream]);

  useEffect(() => {
    return () => {
      void cancelAndPurge();
      stopOwnedStream();
    };
  }, [cancelAndPurge, stopOwnedStream]);

  return {
    archiveState,
    start,
    pause,
    resume,
    cloneStream,
    stopAndUpload,
    cancel,
  };
};
